import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import {
  Octagon, AlertCircle, AlertTriangle, Info, Route, Timer, TrendingDown, Clock,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, ReferenceArea,
} from 'recharts';
import {
  DEFAULT_INPUTS, FIELD_SPECS, ROAD_SURFACE_PRESETS,
  KMPH_TO_MPH, MPH_TO_KMPH, KG_TO_LB, LB_TO_KG, validateField,
} from '../../lib/brakingDistanceTools';
import { brakingDistanceToolApi } from '../../lib/toolsApi';
import { CHART_AXIS } from './simulator/chartTheme';
import ChartZoomHeader from './simulator/ChartZoomHeader';
import { useChartZoom } from './simulator/useChartZoom';

const CALC_DEBOUNCE_MS = 350;
const SPEC_BY_KEY = Object.fromEntries(FIELD_SPECS.map((s) => [s.key, s]));

const ADVANCED_KEYS = [
  'wheelbaseM', 'cgHeightM', 'frontWeightDistPercent', 'frontBrakeForceDistPercent',
  'wheelRadiusM', 'brakeTorqueNm', 'brakeResponseTimeS', 'frontMu', 'rearMu',
];

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const round1 = (n) => Math.round(n * 10) / 10;

function activeKeys(mode) {
  const shared = ['vehicleMassKg', 'initialSpeedKmph', 'reactionTimeS', 'frictionCoefficient', 'gradePercent'];
  return mode === 'advanced' ? [...shared, ...ADVANCED_KEYS] : shared;
}

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" data-testid={`braking-tip-${htmlFor}`} />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const BrakingDistanceCalculator = () => {
  const [mode, setMode] = useState(DEFAULT_INPUTS.mode);
  const [absEnabled, setAbsEnabled] = useState(DEFAULT_INPUTS.absEnabled);
  const [massUnit, setMassUnit] = useState('kg');
  const [speedUnit, setSpeedUnit] = useState('kmph');
  const [roadPreset, setRoadPreset] = useState('dry_asphalt');
  const [frontPreset, setFrontPreset] = useState('dry_asphalt');
  const [rearPreset, setRearPreset] = useState('dry_asphalt');

  // Canonical raw values -- always stored in the backend's base unit (kg, km/h, s, %, m,
  // Nm, mu) regardless of which unit toggle is showing. massField/speedField below
  // convert only at render/onChange time so validation always runs against the spec's
  // (single, base-unit) min/max, same pattern as FuseRatingCalculator's length/temp fields.
  const [raw, setRaw] = useState(() => {
    const out = {};
    Object.keys(SPEC_BY_KEY).forEach((k) => { out[k] = String(DEFAULT_INPUTS[k]); });
    return out;
  });
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (roadPreset !== 'custom') {
      const preset = ROAD_SURFACE_PRESETS.find((p) => p.value === roadPreset);
      if (preset) setField('frictionCoefficient', String(preset.mu));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadPreset]);

  useEffect(() => {
    if (frontPreset !== 'custom') {
      const preset = ROAD_SURFACE_PRESETS.find((p) => p.value === frontPreset);
      if (preset) setField('frontMu', String(preset.mu));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontPreset]);

  useEffect(() => {
    if (rearPreset !== 'custom') {
      const preset = ROAD_SURFACE_PRESETS.find((p) => p.value === rearPreset);
      if (preset) setField('rearMu', String(preset.mu));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rearPreset]);

  const keys = useMemo(() => activeKeys(mode), [mode]);

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const key of keys) {
      const err = validateField(SPEC_BY_KEY[key], raw[key]);
      if (err) errors[key] = err;
      else parsed[key] = Number(raw[key]);
    }
    return { fieldErrors: errors, values: parsed };
  }, [keys, raw]);
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) { setResults(null); setCalcError(null); return; }
    const payload = { mode, absEnabled, ...values };
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      brakingDistanceToolApi.calculate(payload)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => { if (requestId === requestIdRef.current) setCalcError(e.message); });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, values, mode, absEnabled]);

  const numField = (key, { tip, placeholder } = {}) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    const id = `braking-${key}`;
    return (
      <div className="space-y-2" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit} tip={tip}>{spec.label}</FieldLabel>
        <Input
          id={id}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          placeholder={placeholder}
          aria-invalid={!!error}
          data-testid={`${id}-input`}
        />
        {error && (
          <p className="flex items-center gap-1 text-xs text-destructive" data-testid={`${id}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  const massField = () => {
    const error = fieldErrors.vehicleMassKg;
    const canonicalKg = raw.vehicleMassKg;
    const displayValue = massUnit === 'lb' && canonicalKg !== '' && !Number.isNaN(Number(canonicalKg))
      ? String(round1(Number(canonicalKg) * KG_TO_LB))
      : canonicalKg;
    const onChange = (e) => {
      const typed = e.target.value;
      if (massUnit === 'lb') {
        if (typed === '' || typed === '-') { setField('vehicleMassKg', typed); return; }
        const n = Number(typed);
        setField('vehicleMassKg', Number.isNaN(n) ? typed : String(n * LB_TO_KG));
      } else {
        setField('vehicleMassKg', typed);
      }
    };
    return (
      <div className="space-y-2">
        <FieldLabel htmlFor="braking-mass" tip="Total vehicle mass including driver, passengers, and cargo at the time of braking.">
          Vehicle Mass <span className="normal-case text-muted-foreground/70">({massUnit})</span>
        </FieldLabel>
        <div className="flex gap-2">
          <Input id="braking-mass" type="number" step="any" value={displayValue} onChange={onChange} className="font-mono" aria-invalid={!!error} data-testid="braking-vehicleMassKg-input" />
          <Select value={massUnit} onValueChange={setMassUnit}>
            <SelectTrigger className="w-20" data-testid="braking-mass-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="lb">lb</SelectItem></SelectContent>
          </Select>
        </div>
        {error && <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {error}</p>}
      </div>
    );
  };

  const speedField = () => {
    const error = fieldErrors.initialSpeedKmph;
    const canonicalKmph = raw.initialSpeedKmph;
    const displayValue = speedUnit === 'mph' && canonicalKmph !== '' && !Number.isNaN(Number(canonicalKmph))
      ? String(round1(Number(canonicalKmph) * KMPH_TO_MPH))
      : canonicalKmph;
    const onChange = (e) => {
      const typed = e.target.value;
      if (speedUnit === 'mph') {
        if (typed === '' || typed === '-') { setField('initialSpeedKmph', typed); return; }
        const n = Number(typed);
        setField('initialSpeedKmph', Number.isNaN(n) ? typed : String(n * MPH_TO_KMPH));
      } else {
        setField('initialSpeedKmph', typed);
      }
    };
    return (
      <div className="space-y-2">
        <FieldLabel htmlFor="braking-speed" tip="Vehicle speed at the moment the driver perceives the need to stop.">
          Initial Speed <span className="normal-case text-muted-foreground/70">({speedUnit === 'mph' ? 'mph' : 'km/h'})</span>
        </FieldLabel>
        <div className="flex gap-2">
          <Input id="braking-speed" type="number" step="any" value={displayValue} onChange={onChange} className="font-mono" aria-invalid={!!error} data-testid="braking-initialSpeedKmph-input" />
          <Select value={speedUnit} onValueChange={setSpeedUnit}>
            <SelectTrigger className="w-24" data-testid="braking-speed-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="kmph">km/h</SelectItem><SelectItem value="mph">mph</SelectItem></SelectContent>
          </Select>
        </div>
        {error && <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {error}</p>}
      </div>
    );
  };

  const frictionField = (key, presetValue, setPresetValue, label, tip) => {
    const error = fieldErrors[key];
    return (
      <div className="space-y-2">
        <FieldLabel htmlFor={`braking-${key}`} tip={tip}>{label} (μ)</FieldLabel>
        <div className="flex gap-2">
          <Select value={presetValue} onValueChange={setPresetValue}>
            <SelectTrigger className="flex-1" data-testid={`braking-${key}-preset-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROAD_SURFACE_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}{p.mu !== null ? ` (μ ${p.mu})` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {presetValue === 'custom' && (
            <Input id={`braking-${key}`} type="number" step="any" value={raw[key]} onChange={(e) => setField(key, e.target.value)} className="font-mono w-24" aria-invalid={!!error} data-testid={`braking-${key}-input`} />
          )}
        </div>
        {error && <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {error}</p>}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <Octagon className="h-5 w-5 text-autoxium-blue" />
                Braking Distance Calculator
              </CardTitle>
              <CardDescription>
                Estimate reaction, braking, and total stopping distance from friction-limited
                deceleration alone — regenerative braking is out of scope. Switch to Advanced
                for dynamic front/rear weight transfer and brake-system-torque limits.
              </CardDescription>
            </div>
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList data-testid="braking-mode-tabs">
                <TabsTrigger value="basic" data-testid="braking-mode-basic">Basic</TabsTrigger>
                <TabsTrigger value="advanced" data-testid="braking-mode-advanced">Advanced</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
      </Card>

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="technical-card lg:col-span-3">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Vehicle &amp; Road Conditions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-md">
              {massField()}
              {speedField()}
              {numField('reactionTimeS', { tip: 'Time from perceiving the hazard to the brakes beginning to bite — typical alert-driver values are 1.0–2.0 s.' })}
              {numField('gradePercent', { tip: 'Positive = uphill (gravity assists braking, shorter distance). Negative = downhill (gravity opposes braking, longer distance).' })}
            </div>

            <div className="p-4 bg-muted/50 rounded-md space-y-4">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Road Surface</h3>
              {mode === 'basic'
                ? frictionField('frictionCoefficient', roadPreset, setRoadPreset, 'Road Surface / Friction', 'Peak (ABS-achievable) tyre-road friction coefficient. ABS OFF applies a locked-wheel reduction to this value automatically.')
                : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {frictionField('frontMu', frontPreset, setFrontPreset, 'Front Tyre Friction', 'Peak front-axle tyre-road friction coefficient.')}
                    {frictionField('rearMu', rearPreset, setRearPreset, 'Rear Tyre Friction', 'Peak rear-axle tyre-road friction coefficient.')}
                  </div>
                )}
              <div className="flex items-center gap-3 pt-2 border-t border-structure">
                <Switch id="braking-abs" checked={absEnabled} onCheckedChange={setAbsEnabled} data-testid="braking-abs-switch" />
                <Label htmlFor="braking-abs" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  ABS {absEnabled ? 'ON' : 'OFF'}
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild><Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">ABS ON uses the full friction coefficient entered above (modulated braking, no wheel lock). ABS OFF models a locked-wheel skid at 70% of that friction coefficient — a commonly cited kinetic-vs-peak-friction ratio.</TooltipContent>
                </Tooltip>
              </div>
            </div>

            {mode === 'advanced' && (
              <div className="p-4 bg-muted/50 rounded-md space-y-4">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Vehicle Geometry &amp; Weight Distribution</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {numField('wheelbaseM', { tip: 'Distance between the front and rear axle centrelines.' })}
                  {numField('cgHeightM', { tip: 'Height of the vehicle centre of gravity above the ground — drives how much load transfers to the front axle under braking.' })}
                  {numField('frontWeightDistPercent', { tip: 'Static (unbraked) share of vehicle weight carried by the front axle. The rear axle carries the remainder.' })}
                </div>
                {values.frontWeightDistPercent !== undefined && (
                  <p className="text-xs text-muted-foreground">Rear static weight distribution: <span className="font-mono font-bold text-foreground">{fmt(100 - values.frontWeightDistPercent, 1)}%</span></p>
                )}
              </div>
            )}

            {mode === 'advanced' && (
              <div className="p-4 bg-muted/50 rounded-md space-y-4">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Brake System</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {numField('wheelRadiusM', { tip: 'Rolling radius of the wheel/tyre — converts brake torque into a force at the tyre contact patch.' })}
                  {numField('brakeTorqueNm', { tip: 'Total available brake torque across the whole vehicle (all wheels combined), before the friction limit is applied.' })}
                  {numField('frontBrakeForceDistPercent', { tip: 'Share of total available brake force sent to the front axle by the brake proportioning system. The rear axle receives the remainder.' })}
                  {numField('brakeResponseTimeS', { tip: 'Hydraulic/mechanical lag between the brake pedal being pressed and full brake torque being reached — added on top of driver reaction time, both travelled at constant speed.' })}
                </div>
                {values.frontBrakeForceDistPercent !== undefined && (
                  <p className="text-xs text-muted-foreground">Rear brake force distribution: <span className="font-mono font-bold text-foreground">{fmt(100 - values.frontBrakeForceDistPercent, 1)}%</span></p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {calcError ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <div className="flex items-center gap-2 text-sm text-destructive" data-testid="braking-calc-error"><AlertCircle className="h-4 w-4 shrink-0" /> {calcError}</div>
            </CardContent></Card>
          ) : hasErrors ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <p className="text-sm text-muted-foreground" data-testid="braking-results-placeholder">Fix the highlighted fields to see the stopping distance.</p>
            </CardContent></Card>
          ) : !results || results.mode !== mode ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <p className="text-sm text-muted-foreground" data-testid="braking-results-loading">Calculating…</p>
            </CardContent></Card>
          ) : (
            <KpiPanel results={results} speedUnit={speedUnit} />
          )}
        </div>
      </div>

      {results && !hasErrors && results.mode === mode && (
        <ResultsSection results={results} mode={results.mode} speedUnit={speedUnit} />
      )}
    </div>
    </TooltipProvider>
  );
};

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------

const KPI_ICONS = { total: Route, braking: TrendingDown, reaction: Timer, decel: TrendingDown, time: Clock };

const Kpi = ({ icon: Icon, label, value, unit, testId, accent }) => (
  <div className="p-3 bg-muted/50 rounded-md" data-testid={testId}>
    <p className="flex items-center gap-1.5 text-muted-foreground uppercase tracking-wider text-[10px] font-mono mb-1">
      <Icon className="h-3 w-3" /> {label}
    </p>
    <p className={`font-mono text-xl font-black ${accent ? 'text-autoxium-blue' : 'text-foreground'}`}>{value} <span className="text-xs font-normal text-muted-foreground">{unit}</span></p>
  </div>
);

const KpiPanel = ({ results, speedUnit }) => (
  <Card className="technical-card ring-1 ring-autoxium-blue/30" data-testid="braking-results">
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-base">Stopping Distance Summary</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <Kpi icon={KPI_ICONS.total} label="Total Stopping Distance" value={fmt(results.totalDistanceM, 1)} unit="m" testId="braking-kpi-total" accent />
      <div className="grid grid-cols-2 gap-3">
        <Kpi icon={KPI_ICONS.reaction} label="Reaction Distance" value={fmt(results.reactionDistanceM, 1)} unit="m" testId="braking-kpi-reaction" />
        <Kpi icon={KPI_ICONS.braking} label="Braking Distance" value={fmt(results.brakingDistanceM, 1)} unit="m" testId="braking-kpi-braking" />
        <Kpi icon={KPI_ICONS.decel} label="Effective Deceleration" value={fmt(results.effectiveDecelerationMs2, 2)} unit={`m/s² (${fmt(results.effectiveDecelerationG, 2)}g)`} testId="braking-kpi-decel" />
        <Kpi icon={KPI_ICONS.time} label="Stopping Time" value={fmt(results.stoppingTimeS, 2)} unit="s" testId="braking-kpi-time" />
      </div>

      {results.warnings?.length > 0 && (
        <div className="space-y-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 p-3" data-testid="braking-warnings">
          {results.warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-1.5 text-xs text-amber-800 leading-relaxed"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w}</p>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Breakdown bar + charts
// ---------------------------------------------------------------------------

const BreakdownBar = ({ results }) => {
  const total = results.totalDistanceM || 1;
  const reactionPct = (results.reactionDistanceM / total) * 100;
  const brakingPct = (results.brakingDistanceM / total) * 100;
  return (
    <div className="space-y-2" data-testid="braking-breakdown-bar">
      <div className="flex h-8 w-full rounded-md overflow-hidden border border-structure">
        <div className="bg-autoxium-blue/60 flex items-center justify-center text-[10px] font-mono text-white" style={{ width: `${reactionPct}%` }}>
          {reactionPct > 12 ? `${fmt(results.reactionDistanceM, 0)} m` : ''}
        </div>
        <div className="bg-autoxium-blue flex items-center justify-center text-[10px] font-mono text-white" style={{ width: `${brakingPct}%` }}>
          {brakingPct > 12 ? `${fmt(results.brakingDistanceM, 0)} m` : ''}
        </div>
      </div>
      <div className="flex items-center justify-center gap-6 text-xs font-mono">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-autoxium-blue/60 inline-block" /> Reaction ({fmt(results.reactionDistanceM, 1)} m)</span>
        <span className="text-muted-foreground">+</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-autoxium-blue inline-block" /> Braking ({fmt(results.brakingDistanceM, 1)} m)</span>
        <span className="text-muted-foreground">=</span>
        <span className="font-bold">Total ({fmt(results.totalDistanceM, 1)} m)</span>
      </div>
    </div>
  );
};

const SPEED_UNIT_LABEL = { kmph: 'km/h', mph: 'mph' };
const speedToDisplay = (kmph, speedUnit) => (speedUnit === 'mph' ? kmph * KMPH_TO_MPH : kmph);

const SpeedDistanceChart = ({ results, speedUnit }) => {
  const zoom = useChartZoom();
  const data = (results.speedProfile || []).map((p) => ({
    distanceM: p.distanceM,
    speed: round1(speedToDisplay(p.speedKmph, speedUnit)),
  }));
  return (
    <div>
      <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-speed-distance" />
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
            onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
          >
            <defs>
              <linearGradient id="brakingSpeedFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1E40AF" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#1E40AF" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="distanceM" type="number" domain={zoom.xDomain} allowDataOverflow tickFormatter={(v) => fmt(v, 0)} tick={{ fontSize: 10 }} label={{ value: 'Distance (m)', position: 'insideBottom', offset: -4, fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} label={{ value: `Speed (${SPEED_UNIT_LABEL[speedUnit]})`, angle: -90, position: 'insideLeft', fontSize: 11 }} />
            <RechartsTooltip formatter={(v) => [`${fmt(v, 1)} ${SPEED_UNIT_LABEL[speedUnit]}`, 'Speed']} labelFormatter={(v) => `${fmt(v, 1)} m`} />
            <Area type="monotone" dataKey="speed" stroke="#1E40AF" strokeWidth={2} fill="url(#brakingSpeedFill)" dot={false} />
            {zoom.isSelecting && (
              <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const AxleLoadChart = ({ results }) => {
  const data = [
    {
      axle: 'Front',
      'Static Load (N)': results.axleLoadsN.front.static,
      'Dynamic Load (N)': results.axleLoadsN.front.dynamic,
      'Max Tyre-Road Force (N)': results.maxTyreForceN.front,
      'Available Brake Force (N)': results.availableBrakeForceN.front,
    },
    {
      axle: 'Rear',
      'Static Load (N)': results.axleLoadsN.rear.static,
      'Dynamic Load (N)': results.axleLoadsN.rear.dynamic,
      'Max Tyre-Road Force (N)': results.maxTyreForceN.rear,
      'Available Brake Force (N)': results.availableBrakeForceN.rear,
    },
  ];
  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="axle" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 10 }} label={{ value: 'Force (N)', angle: -90, position: 'insideLeft', fontSize: 11 }} />
          <RechartsTooltip formatter={(v) => `${fmt(v, 0)} N`} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="Static Load (N)" fill="#94a3b8" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Dynamic Load (N)" fill="#1E40AF" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Max Tyre-Road Force (N)" fill="#dc2626" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Available Brake Force (N)" fill="#16a34a" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

const ResultsSection = ({ results, mode, speedUnit }) => (
  <div className="space-y-6" data-testid="braking-results-section">
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Reaction + Braking = Total Stopping Distance</CardTitle>
      </CardHeader>
      <CardContent>
        <BreakdownBar results={results} />
      </CardContent>
    </Card>

    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Speed vs. Distance</CardTitle>
        <CardDescription>Constant speed during the reaction/response delay, then constant deceleration to a stop.</CardDescription>
      </CardHeader>
      <CardContent>
        <SpeedDistanceChart results={results} speedUnit={speedUnit} />
      </CardContent>
    </Card>

    {mode === 'advanced' && (
      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Braking Force &amp; Axle Load</CardTitle>
          <CardDescription>Dynamic load includes weight transfer under braking; available brake force is capped at the lesser of brake-system capacity and max tyre-road friction.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <AxleLoadChart results={results} />
          <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-structure text-xs font-mono">
            <div className="flex justify-between"><span className="text-muted-foreground">Front Brake Force</span><span className="font-bold">{fmt(results.availableBrakeForceN.front, 0)} N</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Rear Brake Force</span><span className="font-bold">{fmt(results.availableBrakeForceN.rear, 0)} N</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Front Limited By</span><span className="font-bold capitalize">{results.limitingFactor.front === 'friction' ? 'Tyre-Road Friction' : 'Brake System Torque'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Rear Limited By</span><span className="font-bold capitalize">{results.limitingFactor.rear === 'friction' ? 'Tyre-Road Friction' : 'Brake System Torque'}</span></div>
          </div>
        </CardContent>
      </Card>
    )}
  </div>
);

export default BrakingDistanceCalculator;
